import type { StateCreator } from 'zustand';
import type { StoreState } from '../types/types';
import type { PartyMember, NPC, POI, Encounter, Relationship, EntityType } from '../types/types';
import { api } from '../services/api';
import { logger } from '../services/logger.client';

export interface RosterState {
    partyRoster: PartyMember[]; npcs: NPC[]; pois: POI[];
    relationships: Relationship[];
    encounters: Encounter[]; currentEvents: { id: number; text: string; sortOrder: number; }[];
    weather: { sky: string; precipitation: string; temperature: number; wind: string; };
}

export interface RosterActions {
    addPlayer: (player: Omit<PartyMember, 'id'> & { id?: number }) => Promise<void>;
    deletePlayer: (id: number) => Promise<void>;
    updatePlayer: (player: Partial<PartyMember> & { id: number }) => Promise<void>;
    healPartyMember: (id: number) => void;
    addNPC: (npc: Omit<NPC, 'id' | 'createdAt' | 'updatedAt'> & { id?: number }) => Promise<void>;
    updateNPC: (npc: Partial<NPC> & { id: number }) => Promise<void>;
    deleteNPC: (id: number) => Promise<void>;
    addPOI: (poi: Omit<POI, 'id'> & { id?: number }) => Promise<void>;
    updatePOI: (poi: Partial<POI> & { id: number }) => Promise<void>;
    deletePOI: (id: number) => Promise<void>;
    addRelationship: (entity1Id: number, entity1Type: EntityType, entity2Id: number, entity2Type: EntityType, type: string, notes: string | null) => Promise<boolean>;
    deleteRelationship: (id: number) => Promise<void>;
    updateRelationship: (relationship: Partial<Relationship> & { id: number }) => Promise<void>;
    addEncounter: (encounter: Omit<Encounter, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
    updateEncounter: (encounter: Partial<Encounter> & { id: number; }) => Promise<void>;
    deleteEncounter: (id: number) => Promise<void>;
    addCurrentEvent: (text: string) => Promise<void>; // Creates a new event
    deleteCurrentEvent: (id: number) => Promise<void>; // Deletes an event by its numeric ID
    updateCurrentEventsSort: (newEvents: { id: number; text: string; sortOrder: number; }[]) => Promise<void>;
    updateTime: (minutes: number) => void;
    setWeather: (newWeather: Partial<RosterState['weather']>) => void;
}

export type RosterSlice = RosterState & RosterActions;

export const createRosterActions: StateCreator<StoreState, [['zustand/immer', never]], [], { state: RosterState, actions: RosterActions }> = (set, get) => ({
    state: {
        partyRoster: [],
        npcs: [], pois: [], relationships: [], encounters: [],
        currentEvents: [],
        weather: { sky: 'clear', precipitation: 'none', temperature: 25, wind: 'light' },
    },
    actions: {
        addPlayer: async (player) => {
            const tempId = -Date.now();
            const tempPlayer = { ...player, id: tempId };
            set(state => { state.session.roster.partyRoster.push(tempPlayer as PartyMember); });

            try {
                const response = await api('/api/players', { method: 'POST', body: JSON.stringify(player) });
                const newPlayer = await response.json() as PartyMember;
                set(state => {
                    const index = (state.session.roster.partyRoster ?? []).findIndex((p: PartyMember) => p.id === tempId);
                    if (index !== -1) state.session.roster.partyRoster[index] = newPlayer;
                });
            } catch (error) {
                logger.error(error, 'Error adding player, rolling back.');
                set(state => { state.session.roster.partyRoster = (state.session.roster.partyRoster ?? []).filter((p: PartyMember) => p.id !== tempId); });
                throw error;
            }
        },
        deletePlayer: async (id) => {
            const originalRoster = get().session.roster.partyRoster;
            set(state => { state.session.roster.partyRoster = (originalRoster ?? []).filter((p: PartyMember) => p.id !== id); });

            try {
                await api(`/api/players/${id}`, { method: 'DELETE' });
            } catch (error) {
                logger.error(error, `Error deleting player ${id}, rolling back.`);
                set(state => { state.session.roster.partyRoster = originalRoster; });
                throw error;
            }
        },
        updatePlayer: async (player) => {
            const originalRoster = get().session.roster.partyRoster;
            const playerIndex = (originalRoster ?? []).findIndex((p: PartyMember) => p.id === player.id);

            if (playerIndex === -1) {
                logger.error(`Player with id ${player.id} not found for update.`);
                return;
            }

            // Optimistically update the state
            set(state => {
                state.session.roster.partyRoster[playerIndex] = { ...state.session.roster.partyRoster[playerIndex]!, ...player };
            });

            try {
                const response = await api(`/api/players/${player.id}`, { method: 'PUT', body: JSON.stringify(player) });
                const updatedPlayerFromServer = await response.json() as PartyMember;
                // After successful API call, update the state with the authoritative data from the server
                set(state => {
                    const index = (state.session.roster.partyRoster ?? []).findIndex((p: PartyMember) => p.id === player.id);
                    if (index !== -1) state.session.roster.partyRoster[index] = updatedPlayerFromServer;
                });
            } catch (error) {
                logger.error(error, `Error updating player ${player.id}, rolling back.`);
                // Rollback on error
                set(state => { state.session.roster.partyRoster = originalRoster; });
                throw error; // Re-throw so the UI can handle it
            }
        },
        healPartyMember: (id) => { /* This is a local-only action, no API call needed */
            set(store => {
                const player = (store.session.roster.partyRoster ?? []).find((p: PartyMember) => p.id === id);
                if (player) player.hp = player.maxHp;
            });
        },
        addNPC: async (npc) => {
            const tempId = -Date.now();
            const tempNpc = { ...npc, id: tempId };
            set(state => { state.session.roster.npcs.push(tempNpc as NPC); });

            try {
                const response = await api('/api/npcs', { method: 'POST', body: JSON.stringify(npc) });
                const newNpc = await response.json() as NPC;
                set(state => {
                    const index = (state.session.roster.npcs ?? []).findIndex((n: NPC) => n.id === tempId);
                    if (index !== -1) state.session.roster.npcs[index] = newNpc;
                });
            } catch (error) {
                logger.error(error, 'Error adding NPC, rolling back.');
                set(state => { state.session.roster.npcs = (state.session.roster.npcs ?? []).filter((n: NPC) => n.id !== tempId); });
                throw error;
            }
        },
        updateNPC: async (npc) => {
            const originalNpcs = get().session.roster.npcs;
            const npcIndex = (originalNpcs ?? []).findIndex((n: NPC) => n.id === npc.id);
            if (npcIndex === -1) return;

            set(state => { state.session.roster.npcs[npcIndex] = { ...state.session.roster.npcs[npcIndex]!, ...npc }; });

            try {
                await api(`/api/npcs/${npc.id}`, { method: 'PUT', body: JSON.stringify(npc) });
            } catch (error) {
                logger.error(error, `Error updating NPC ${npc.id}, rolling back.`);
                set(state => { state.session.roster.npcs = originalNpcs; });
                throw error;
            }
        },
        deleteNPC: async (id) => {
            const originalNpcs = get().session.roster.npcs;
            set(state => { state.session.roster.npcs = (state.session.roster.npcs ?? []).filter((n: NPC) => n.id !== id); });

            try {
                await api(`/api/npcs/${id}`, { method: 'DELETE' });
            } catch (error) {
                logger.error(error, `Error deleting NPC ${id}, rolling back.`);
                set(state => { state.session.roster.npcs = originalNpcs; });
                throw error;
            }
        },
        addPOI: async (poi) => {
            const tempId = -Date.now();
            const tempPoi = { ...poi, id: tempId };
            set(state => { state.session.roster.pois.push(tempPoi as POI); });

            try {
                const response = await api('/api/pois', { method: 'POST', body: JSON.stringify(poi) });
                const newPoi = await response.json() as POI;
                set(state => {
                    const index = (state.session.roster.pois ?? []).findIndex((p: POI) => p.id === tempId);
                    if (index !== -1) state.session.roster.pois[index] = newPoi;
                });
            } catch (error) {
                logger.error(error, 'Error adding POI, rolling back.');
                set(state => { state.session.roster.pois = (state.session.roster.pois ?? []).filter((p: POI) => p.id !== tempId); });
                throw error;
            }
        },
        updatePOI: async (poi) => {
            const originalPois = get().session.roster.pois;
            const poiIndex = (originalPois ?? []).findIndex((p: POI) => p.id === poi.id);
            if (poiIndex === -1) return;

            set(state => { state.session.roster.pois[poiIndex] = { ...state.session.roster.pois[poiIndex]!, ...poi }; });

            try {
                await api(`/api/pois/${poi.id}`, { method: 'PUT', body: JSON.stringify(poi) });
            } catch (error) {
                logger.error(error, `Error updating POI ${poi.id}, rolling back.`);
                set(state => { state.session.roster.pois = originalPois; });
                throw error;
            }
        },
        deletePOI: async (id) => {
            const originalPois = get().session.roster.pois;
            set(state => { state.session.roster.pois = (state.session.roster.pois ?? []).filter((p: POI) => p.id !== id); });

            try {
                await api(`/api/pois/${id}`, { method: 'DELETE' });
            } catch (error) {
                logger.error(error, `Error deleting POI ${id}, rolling back.`);
                set(state => { state.session.roster.pois = originalPois; });
                throw error;
            }
        },
        addRelationship: async (entity1Id, entity1Type, entity2Id, entity2Type, type, notes): Promise<boolean> => {
            const exists = (get().session.roster.relationships ?? []).some((r: Relationship) =>
                (r.entity1Id === entity1Id && r.entity1Type === entity1Type && r.entity2Id === entity2Id && r.entity2Type === entity2Type && r.type === type) ||
                (r.entity1Id === entity2Id && r.entity1Type === entity2Type && r.entity2Id === entity1Id && r.entity2Type === entity1Type) // Check for inverse relationship
            );
            if (exists) return false;

            const tempId = -Date.now();
            // Create a full temporary object to satisfy the type, including dates.
            const tempRelationship = { id: tempId, entity1Id, entity1Type, entity2Id, entity2Type, type, notes, createdAt: new Date(), updatedAt: new Date() };
            set(state => {
                state.session.roster.relationships.push(tempRelationship);
            });

            try {
                const response = await api('/api/relationships', { method: 'POST', body: JSON.stringify({ entity1Id, entity1Type, entity2Id, entity2Type, type, notes }) });
                const newRelationship = await response.json() as Relationship;
                set(state => {
                    const index = (state.session.roster.relationships ?? []).findIndex((r: Relationship) => r.id === tempId);
                    if (index !== -1) state.session.roster.relationships[index] = newRelationship;
                });
                return true;
            } catch (error) {
                logger.error(error, 'Error adding relationship');
                set(state => {
                    state.session.roster.relationships = (state.session.roster.relationships ?? []).filter((r: Relationship) => r.id !== tempId);
                });
                throw error;
            }
        },
        deleteRelationship: async (id: number) => {
            const originalRelationships = get().session.roster.relationships;
            set(state => { state.session.roster.relationships = (originalRelationships ?? []).filter((r: Relationship) => r.id !== id); });

            try {
                await api(`/api/relationships/${id}`, { method: 'DELETE' });
            } catch (error) {
                logger.error(error, `Error deleting relationship ${id}, rolling back.`);
                set(state => { state.session.roster.relationships = originalRelationships; });
                throw error;
            }
        },
        updateRelationship: async (relationship) => {
            const originalRelationships = get().session.roster.relationships;
            const relationshipIndex = (originalRelationships ?? []).findIndex((r: Relationship) => r.id === relationship.id);
            if (relationshipIndex === -1) return;

            set(state => { state.session.roster.relationships[relationshipIndex] = { ...state.session.roster.relationships[relationshipIndex]!, ...relationship }; });

            try {
                await api(`/api/relationships/${relationship.id}`, { method: 'PUT', body: JSON.stringify(relationship) });
            } catch (error) {
                logger.error(error, `Error updating relationship ${relationship.id}, rolling back.`);
                set(state => { state.session.roster.relationships = originalRelationships; });
                throw error;
            }
        },
        addEncounter: async (encounter) => {
            const tempId = -Date.now();
            const tempEncounter = { ...encounter, id: tempId };
            set(state => { state.session.roster.encounters.push(tempEncounter as Encounter); });

            try {
                const response = await api('/api/encounters', { method: 'POST', body: JSON.stringify(encounter) });
                const newEncounter = await response.json() as Encounter;
                set(state => {
                    const index = (state.session.roster.encounters ?? []).findIndex((e: Encounter) => e.id === tempId);
                    if (index !== -1) state.session.roster.encounters[index] = newEncounter;
                });
            } catch (error) {
                logger.error(error, 'Error adding encounter, rolling back.');
                set(state => { state.session.roster.encounters = (state.session.roster.encounters ?? []).filter((e: Encounter) => e.id !== tempId); });
                throw error;
            }
        },
        updateEncounter: async (encounter) => {
            const originalEncounters = get().session.roster.encounters;
            const encounterIndex = (originalEncounters ?? []).findIndex((e: Encounter) => e.id === encounter.id);
            if (encounterIndex === -1) return;

            set(state => { state.session.roster.encounters[encounterIndex] = { ...state.session.roster.encounters[encounterIndex]!, ...encounter }; });

            try {
                await api(`/api/encounters/${encounter.id}`, { method: 'PUT', body: JSON.stringify(encounter) });
            } catch (error) {
                logger.error(error, `Error updating encounter ${encounter.id}, rolling back.`);
                set(state => { state.session.roster.encounters = originalEncounters; });
                throw error;
            }
        },
        deleteEncounter: async (id) => {
            const originalEncounters = get().session.roster.encounters;
            set(state => { state.session.roster.encounters = (originalEncounters ?? []).filter((e: Encounter) => e.id !== id); });

            try {
                await api(`/api/encounters/${id}`, { method: 'DELETE' });
            } catch (error) {
                logger.error(error, `Error deleting encounter ${id}, rolling back.`);
                set(state => { state.session.roster.encounters = originalEncounters; });
                throw error;
            }
        },
        addCurrentEvent: async (text) => {
            const tempId = -Date.now();
            const tempEvent = { id: tempId, text, sortOrder: get().session.roster.currentEvents.length };
            set(state => {
                state.session.roster.currentEvents.push(tempEvent as { id: number; text: string; sortOrder: number; });
            });

            try {
                const response = await api('/api/current-events', { method: 'POST', body: JSON.stringify({ text }) });
                const newEvent = await response.json() as { id: number, text: string, sortOrder: number };
                set(state => {
                    const index = (state.session.roster.currentEvents ?? []).findIndex(e => e.id === tempId);
                    if (index !== -1) state.session.roster.currentEvents[index] = newEvent;
                });
            } catch (error) {
                logger.error(error, 'Error adding current event');
                set(state => {
                    state.session.roster.currentEvents = (state.session.roster.currentEvents ?? []).filter(e => e.id !== tempId);
                });
                throw error;
            }
        },
        deleteCurrentEvent: async (id: number) => {
            const originalEvents = get().session.roster.currentEvents;
            set(state => { state.session.roster.currentEvents = (originalEvents ?? []).filter(e => e.id !== id); });

            try {
                await api(`/api/current-events/${id}`, { method: 'DELETE' });
            } catch (error) {
                logger.error(error, `Error deleting current event ${id}, rolling back.`);
                set(state => { state.session.roster.currentEvents = originalEvents; });
                throw error;
            }
        },
        updateCurrentEventsSort: async (newEvents) => {
            const originalEvents = get().session.roster.currentEvents;
            set(state => { state.session.roster.currentEvents = newEvents; });

            try {
                const orderedIds = (newEvents ?? []).map(e => e.id);
                await api('/api/current-events/sort', { method: 'PUT', body: JSON.stringify({ orderedIds }) });
            } catch (err) {
                logger.error(err, 'Failed to save event order, rolling back.');
                set(state => { state.session.roster.currentEvents = originalEvents; });
                throw err;
            }
        },
        updateTime: (minutes) => set(store => {
            if (store.session.general.gameTime) {
                store.session.general.gameTime = new Date(store.session.general.gameTime.getTime() + minutes * 60000);
            } else {
                logger.warn('Attempted to update time, but gameTime is not set.');
            }
        }),
        setWeather: (newWeather) => set(store => {
            store.session.roster.weather = { ...store.session.roster.weather, ...newWeather };
        }),
    }
});